/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/

import * as React from 'react';
import { getOverride, getOverrideProps } from '../helpers/overrides';
import { UIDConsumer } from 'react-uid';
import { Alert, Check } from '../icon';
import { Label as StyledLabel, LabelEndEnhancer as StyledLabelEndEnhancer, LabelContainer as StyledLabelContainer, Caption as StyledCaption, ControlContainer as StyledControlContainer, CaptionMessage as StyledCaptionMessage, CaptionIcon as StyledCaptionIcon } from './styled-components';
import type { FormControlProps, FormControlState, StyleProps } from './types';
function chooseRenderedHint(caption, error, positive, sharedProps) {
  if (!!error && typeof error !== 'boolean') {
    return typeof error === 'function' ? error(sharedProps) : error;
  }
  if (!!positive && typeof positive !== 'boolean') {
    return typeof positive === 'function' ? positive(sharedProps) : positive;
  }
  if (caption) {
    return typeof caption === 'function' ? caption(sharedProps) : caption;
  }
  return null;
}

// @ts-ignore
function chooseRenderedHintIcon(hint, error, positive) {
  if (!hint) {
    return null;
  }
  if (!!error) {
    if (typeof error === 'function') {
      return null;
    }
    return <Alert size={12} aria-hidden="true" title="" />;
  }
  if (!!positive) {
    if (typeof positive === 'function') {
      return null;
    }
    return <Check size={12} aria-hidden="true" title="" />;
  }
  return null;
}
export default class FormControl extends React.Component<FormControlProps, FormControlState> {
  static defaultProps = {
    overrides: {},
    // @ts-ignore
    label: null,
    // @ts-ignore
    caption: null,
    disabled: false,
    counter: false
  };
  render() {
    const {
      overrides: {
        // @ts-ignore
        Label: LabelOverride,
        // @ts-ignore
        LabelEndEnhancer: LabelEndEnhancerOverride,
        // @ts-ignore
        LabelContainer: LabelContainerOverride,
        // @ts-ignore
        Caption: CaptionOverride,
        // @ts-ignore
        CaptionMessage: CaptionMessageOverride,
        // @ts-ignore
        CaptionIcon: CaptionIconOverride,
        // @ts-ignore
        ControlContainer: ControlContainerOverride
      },
      label,
      caption,
      disabled,
      error,
      positive,
      htmlFor,
      children,
      counter,
      passesExternalLabel
    } = this.props;

    // @ts-expect-error todo(flow->ts): looks dangerous
    const onlyChildProps = React.Children.only(children).props;
    const sharedProps: StyleProps = {
      $disabled: !!disabled,
      $error: !!error,
      $positive: !!positive
    };
    const Label = getOverride(LabelOverride) || StyledLabel;
    const LabelEndEnhancer = getOverride(LabelEndEnhancerOverride) || StyledLabelEndEnhancer;
    const LabelContainer = getOverride(LabelContainerOverride) || StyledLabelContainer;
    const Caption = getOverride(CaptionOverride) || StyledCaption;
    const CaptionMessage = getOverride(CaptionMessageOverride) || StyledCaptionMessage;
    const CaptionIcon = getOverride(CaptionIconOverride) || StyledCaptionIcon;
    const ControlContainer = getOverride(ControlContainerOverride) || StyledControlContainer;
    const hint = chooseRenderedHint(caption, error, positive, sharedProps);
    const hintIcon = chooseRenderedHintIcon(hint, error, positive);
    if (__DEV__) {
      if (error && positive) {
        console.warn(`[FormControl] \`error\` and \`positive\` are both set to \`true\`. \`error\` will take precedence but this may not be what you want.`);
      }
    }
    let labelEndEnhancer = this.props.labelEndEnhancer;
    if (counter) {
      // inferred values are preferred but if the user specifies the value
      // that is then used as the default.
      let maxLength: number | undefined | null = null;
      let length: number | undefined | null = null;
      let counterError: boolean | undefined | null = null;
      if (typeof counter === 'object') {
        length = counter.length;
        maxLength = counter.maxLength;
        counterError = counter.error;
      }
      maxLength = maxLength ? maxLength : onlyChildProps.maxLength;
      if (length == null && typeof onlyChildProps.value === 'string') {
        length = onlyChildProps.value.length;
      }
      if (length == null) {
        length = 0;
        if (__DEV__) {
          console.warn(`[FromControl] \`length\` must either be explicitly set via \`counter\` object property, or \`value\` string property on the child component.`);
        }
      }
      sharedProps.$length = length;
      if (maxLength == null) {
        if (!labelEndEnhancer) labelEndEnhancer = `${length}`;
      } else {
        sharedProps.$maxLength = length;
        if (!labelEndEnhancer) labelEndEnhancer = `${length}/${maxLength}`;
        if (length > maxLength && counterError == null) counterError = true;
      }
      if (counterError) {
        sharedProps.$error = true;
        sharedProps.$counterError = true;
      }
    }

    // Inner render helper shared by both the passesExternalLabel and default paths.
    // labelId is only defined when passesExternalLabel is true.
    const renderContent = (labelId?: string) => <React.Fragment>
        {label && <LabelContainer {...sharedProps} {...getOverrideProps(LabelContainerOverride)}>
            <Label {...labelId ? {
          id: labelId
        } : {}} data-baseweb="form-control-label" htmlFor={htmlFor || onlyChildProps.id} {...sharedProps} {...getOverrideProps(LabelOverride)}>
              {typeof label === 'function' ? label(sharedProps) : label}
            </Label>
            {!!labelEndEnhancer && <LabelEndEnhancer {...sharedProps} {...getOverrideProps(LabelEndEnhancerOverride)}>
                {typeof labelEndEnhancer === 'function' ? labelEndEnhancer(sharedProps) : labelEndEnhancer}
              </LabelEndEnhancer>}
          </LabelContainer>}
        <UIDConsumer>
          {(captionId: string) => <ControlContainer data-baseweb="form-control-container" {...sharedProps} {...getOverrideProps(ControlContainerOverride)}>
              {React.Children.map(children, (child, index) => {
            if (!child) return;

            // @ts-expect-error
            const key = child.key || String(index);
            // When passesExternalLabel is set, FormControl owns the external-label
            // wiring: it injects the generated label ID via aria-labelledby AND
            // turns on the child's receivesExternalLabel gate so consumers only
            // need to opt in once at the FormControl level.
            const shouldWireExternalLabel = !!passesExternalLabel && !!labelId && !!label && !onlyChildProps['aria-labelledby'];
            // @ts-expect-error todo(flow->ts): looks dangerous -- can be possible error
            return React.cloneElement(child, {
              key,
              ...(shouldWireExternalLabel ? {
                'aria-labelledby': labelId
              } : {}),
              ...(shouldWireExternalLabel && onlyChildProps.receivesExternalLabel === undefined ? {
                receivesExternalLabel: true
              } : {}),
              'aria-errormessage': error ? captionId : null,
              'aria-describedby': caption || positive ? captionId : null,
              disabled: onlyChildProps.disabled || disabled,
              error: typeof onlyChildProps.error !== 'undefined' ? onlyChildProps.error : sharedProps.$error === false ? undefined : sharedProps.$error,
              positive: typeof onlyChildProps.positive !== 'undefined' ? onlyChildProps.positive : sharedProps.$positive === false ? undefined : sharedProps.$positive
            });
          })}
              {hint && <Caption data-baseweb="form-control-caption" id={captionId} {...sharedProps} {...getOverrideProps(CaptionOverride)}>
                  {hintIcon && <CaptionIcon>{hintIcon}</CaptionIcon>}
                  <CaptionMessage>{hint}</CaptionMessage>
                </Caption>}
            </ControlContainer>}
        </UIDConsumer>
      </React.Fragment>;

    // When passesExternalLabel is true, wrap in a UIDConsumer to generate a stable
    // label ID that is given to the <Label> element and injected into children via
    // aria-labelledby (WCAG 2.5.3). When false (the default), the original render
    // path is used so existing consumers are not affected.
    if (passesExternalLabel) {
      return <UIDConsumer>{(labelId: string) => renderContent(labelId)}</UIDConsumer>;
    }
    return renderContent();
  }
}